import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [videoModalOpen, setVideoModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-24">
            <div className="flex-shrink-0">
              <img 
                src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/c02c3446a2b3e29ad54a764122882bc0.png" 
                alt="NexGen Steel" 
                className="h-60"
              />
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <button 
                onClick={() => scrollToSection('features')}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}
              >
                The Process
              </button>
              <Link 
                to="/about"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}
              >
                About
              </Link>
              <button 
                onClick={() => scrollToSection('services')}
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}
              >
                Services
              </button>
              <Link 
                to="/resources"
                className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-700 hover:text-gray-900' : 'text-white hover:text-gray-200'}`}
              >
                Resources
              </Link>
              <button 
                onClick={() => scrollToSection('contact')}
                className="bg-amber-500 text-gray-900 px-6 py-2.5 rounded-lg text-sm font-semibold hover:bg-amber-400 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center"
              >
                Contact Us
              </button>
            </div>

            <button 
              className="md:hidden cursor-pointer"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl ${isScrolled ? 'text-gray-900' : 'text-white'}`}></i>
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-4 space-y-3">
              <button 
                onClick={() => scrollToSection('features')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                The Process
              </button>
              <Link 
                to="/about"
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                About
              </Link>
              <button 
                onClick={() => scrollToSection('services')}
                className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
              >
                Services
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="block w-full text-left px-4 py-2 bg-amber-500 text-gray-900 hover:bg-amber-400 rounded-lg font-semibold cursor-pointer whitespace-nowrap"
              >
                Contact Us
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/9068c87526ddbd99c933f0ca5c658468.jpeg"
            alt="ICF Construction"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            POLYCRETE<sup className="text-3xl md:text-4xl">®</sup>
            <br />
            <span className="block mt-4 text-3xl md:text-5xl">26 years of Mastering</span>
            <span className="block">High Performance ICF Systems</span>
          </h1>
          <p className="text-xl md:text-2xl text-white mb-10 max-w-4xl mx-auto leading-relaxed">
            Switch from CMU or wood framing to precision-engineered ICF system. Speed up builds, and material waste.
          </p>
          <button 
            onClick={() => scrollToSection('contact')}
            className="bg-amber-500 text-gray-900 px-8 py-2.5 rounded-lg text-lg font-bold hover:bg-amber-400 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap inline-flex items-center"
          >
            Get My Free Estimate
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <i className="ri-time-line text-4xl text-amber-500"></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">1 DAY</div>
                <div className="text-sm text-gray-600 uppercase tracking-wide">Pre-Cut Factory Service</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <i className="ri-tools-line text-4xl text-amber-500"></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">5-10 DAYS</div>
                <div className="text-sm text-gray-600 uppercase tracking-wide">On-site assembly</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <i className="ri-shield-check-line text-4xl text-amber-500"></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">20-40%</div>
                <div className="text-sm text-gray-600 uppercase tracking-wide">Typical insurance savings</div>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center">
                  <i className="ri-recycle-line text-4xl text-amber-500"></i>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">2% WASTE</div>
                <div className="text-sm text-gray-600 uppercase tracking-wide">Pre-engineered panels</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/5f47c77c836e004333a2d82b946d5e21.jpeg" 
            alt="Process Background" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-white mb-6">THE PROCESS</h2>
            <h3 className="text-2xl font-semibold text-white mb-4">From concept to concrete, NexblocX simplifies ICF systems.</h3>
            <p className="text-lg text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Our turn-key process includes blueprint optimization, factory pre-cut wall systems, pre-engineered openings, membrane coordination, and expert on-site support—delivering a seamless, efficient, and high-performance build experience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow group">
              <div className="absolute top-4 left-4 z-10 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-gray-900 font-bold text-lg">1</span>
              </div>
              <div className="hidden lg:flex absolute top-1/2 -right-5 z-10 w-8 h-8 items-center justify-center text-white">
                <i className="ri-arrow-right-line text-2xl"></i>
              </div>
              <div className="aspect-square w-full h-64">
                <img 
                  src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/a591bfba5cc57b27a4f40163462df412.jpeg" 
                  alt="Plan Conversion" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">Analyze your plans and CAD set up</h4>
              </div>
            </div>

            <div className="relative bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow group">
              <div className="absolute top-4 left-4 z-10 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-gray-900 font-bold text-lg">2</span>
              </div>
              <div className="hidden lg:flex absolute top-1/2 -right-5 z-10 w-8 h-8 items-center justify-center text-white">
                <i className="ri-arrow-right-line text-2xl"></i>
              </div>
              <div className="aspect-square w-full h-64">
                <img 
                  src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/07ef8cd33d37eb04c5d812bf32d352cd.jpeg" 
                  alt="3D Steel Printing" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">factory pre-cutting service</h4>
              </div>
            </div>

            <div className="relative bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow group">
              <div className="absolute top-4 left-4 z-10 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-gray-900 font-bold text-lg">3</span>
              </div>
              <div className="hidden lg:flex absolute top-1/2 -right-5 z-10 w-8 h-8 items-center justify-center text-white">
                <i className="ri-arrow-right-line text-2xl"></i>
              </div>
              <div className="aspect-square w-full h-64">
                <img 
                  src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/e1580c0d55bc2c04e4e1d596e795ee54.jpeg" 
                  alt="Factory Production" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">factory production of the project and packaging</h4>
              </div>
            </div>

            <div className="relative bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow group">
              <div className="absolute top-4 left-4 z-10 w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-gray-900 font-bold text-lg">4</span>
              </div>
              <div className="aspect-square w-full h-64">
                <img 
                  src="https://assets.cdn.filesafe.space/mmyEZebuMGruHUfsH8f7/media/6e04f3c2-572a-478e-b3f0-9fa27e360704.webp" 
                  alt="Delivery" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-6 text-center">
                <h4 className="text-xl font-bold text-gray-900">Delivery and project support on jobsite</h4>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Why Choose NexblocX?</h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                NexBlocX by Polycrete is redefining modern construction with pre-engineered insulated concrete form (ICF) building systems designed for strength, efficiency, and long-term performance. Our advanced engineering and manufacturing process transforms architectural plans into precision-prepared ICF solutions that outperform traditional framing methods in durability, energy efficiency, and resilience.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                We are committed to helping builders reduce construction risk, accelerate project timelines, and lower lifecycle costs, while delivering high-performance structures with a reduced environmental footprint.
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-2xl text-green-600"></i>
                  </div>
                  <div className="ml-3">
                    <h4 className="font-semibold text-gray-900 mb-1">Cost Effective</h4>
                    <p className="text-sm text-gray-600">Lower material and labor costs</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-2xl text-green-600"></i>
                  </div>
                  <div className="ml-3">
                    <h4 className="font-semibold text-gray-900 mb-1">Fast Installation</h4>
                    <p className="text-sm text-gray-600">Rapid on-site assembly</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-2xl text-green-600"></i>
                  </div>
                  <div className="ml-3">
                    <h4 className="font-semibold text-gray-900 mb-1">Eco-Friendly</h4>
                    <p className="text-sm text-gray-600">Zero material waste</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-double-line text-2xl text-green-600"></i>
                  </div>
                  <div className="ml-3">
                    <h4 className="font-semibold text-gray-900 mb-1">Superior Strength</h4>
                    <p className="text-sm text-gray-600">Engineered for durability</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square w-full h-full rounded-lg overflow-hidden shadow-2xl">
                <img 
                  src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/8c2c0f739876d9f363acecd57ba312b9.jpeg" 
                  alt="NexGen Steel Team" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Our Services</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Solution systems tailored to your project needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-draft-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">ReDesign blueprint</h3>
              <p className="text-gray-600 leading-relaxed">
                We transform architectural plans into optimized, build-ready ICF systems engineered for structural performance, efficiency, and code compliance.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-calculator-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Expert Guidance & Consultation</h3>
              <p className="text-gray-600 leading-relaxed">
                Expert guidance throughout your project lifecycle, from initial planning to final installation and beyond.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-scissors-cut-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Pre-Cut Manufacturing</h3>
              <p className="text-gray-600 leading-relaxed">
                Precision pre-cut ICF components—including walls, door, and window openings—are factory-prepared to ensure accuracy, speed, and reduced waste on site.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-truck-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Delivery & Logistics</h3>
              <p className="text-gray-600 leading-relaxed">
                Coordinated logistics and dependable delivery schedules ensure materials arrive on time, in sequence, and ready for immediate installation.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-team-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Installation Support</h3>
              <p className="text-gray-600 leading-relaxed">
                We collaborate closely with architects, builders, and developers to align design intent, constructability, and performance objectives from concept to completion.
              </p>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 bg-amber-100 rounded-lg flex items-center justify-center mb-6">
                <i className="ri-calculator-line text-3xl text-amber-600"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Project Consultation</h3>
              <p className="text-gray-600 leading-relaxed">
                Expert guidance throughout your project lifecycle, from initial planning to final installation and beyond.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/5f47c77c836e004333a2d82b946d5e21.jpeg" 
            alt="Construction Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Compare Your Build with Our System</h2>
          <p className="text-xl text-white mb-10 leading-relaxed">
            Upload your plans and our team will generate a detailed steel framing proposal - including pricing, timeline, and material specifications - so you can evaluate your options with confidence.
          </p>
          <button 
            onClick={() => scrollToSection('contact')}
            className="bg-amber-500 text-gray-900 px-8 py-4 rounded-lg text-lg font-bold hover:bg-amber-400 transition-all transform hover:scale-105 cursor-pointer whitespace-nowrap inline-flex items-center"
          >
            Submit Plans
            <i className="ri-arrow-right-line ml-2"></i>
          </button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">Get Your Free Estimate</h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Upload your plans and get a free, no-obligation estimate showing how steel pricing stacks up against your current lumber design.
              </p>
              
              <div className="aspect-video w-full rounded-lg overflow-hidden shadow-lg mb-8">
                <div className="relative w-full h-full bg-gray-900">
                  <img 
                    src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/ccae3c13045ffe604b44987c36f4a8cf.jpeg" 
                    alt="NexGen Steel Video" 
                    className="w-full h-full object-cover"
                  />
                  <div 
                    className="absolute inset-0 flex items-center justify-center"
                    onClick={() => setVideoModalOpen(true)}
                  >
                    <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center cursor-pointer hover:bg-white transition-colors hover:scale-110 transform duration-300">
                      <i className="ri-play-fill text-3xl text-gray-900 ml-1"></i>
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-2xl text-amber-500"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900">Email Us</h4>
                    <p className="text-gray-600">info@nexblocx.com</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-2xl text-amber-500"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900">Call Us</h4>
                    <p className="text-gray-600">561-918-2055</p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-2xl text-amber-500"></i>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-semibold text-gray-900">Business Hours</h4>
                    <p className="text-gray-600">Monday to Friday</p>
                    <p className="text-gray-600">8:00 AM - 4:00 PM</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-8 shadow-lg">
              <form 
                id="contact-form" 
                data-readdy-form 
                action="https://readdy.ai/api/form/d5v84fkkjhtioh5js320"
                method="POST"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const formData = new FormData(form);
                  const textarea = form.querySelector('textarea');
                  if (textarea && textarea.value.length > 500) {
                    alert('Message must be 500 characters or less');
                    return;
                  }
                  
                  try {
                    const response = await fetch('https://readdy.ai/api/form/d5v84fkkjhtioh5js320', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                      },
                      body: new URLSearchParams(formData as any).toString(),
                    });
                    
                    if (response.ok) {
                      alert('Thank you! Your submission has been received.');
                      form.reset();
                    } else {
                      alert('Something went wrong. Please try again.');
                    }
                  } catch (error) {
                    alert('Something went wrong. Please try again.');
                  }
                }}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="builder-name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Builder / Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="builder-name"
                      name="builder-name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      Builder Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Builder Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="project-name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Project Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="project-name"
                      name="project-name"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="project-location" className="block text-sm font-semibold text-gray-900 mb-2">
                      Project Location (City, State) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="project-location"
                      name="project-location"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="project-type" className="block text-sm font-semibold text-gray-900 mb-2">
                      Project Type <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="project-type"
                      name="project-type"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label htmlFor="start-date" className="block text-sm font-semibold text-gray-900 mb-2">
                      Estimated Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      id="start-date"
                      name="start-date"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                    Additional Information
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
                    placeholder="Tell us more about your project..."
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="terms"
                    name="terms"
                    required
                    className="mt-1 cursor-pointer"
                  />
                  <label htmlFor="terms" className="ml-3 text-sm text-gray-600 cursor-pointer">
                    By checking this box, I agree to receive transactional text messages related to my account, orders, or services requested. Message & Data rates may apply. Reply HELP for help or STOP to opt-out.
                  </label>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gray-900 text-white px-6 py-4 rounded-lg text-base font-bold hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Submit
                </button>

                <p className="text-xs text-center text-gray-500">
                  <Link to="/privacy" className="hover:text-gray-700">Privacy Policy</Link>
                  {' | '}
                  <Link to="/terms" className="hover:text-gray-700">Terms of Service</Link>
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Video Modal */}
      {videoModalOpen && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={() => setVideoModalOpen(false)}
        >
          <div 
            className="relative w-full max-w-4xl mx-4 aspect-video bg-black rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setVideoModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-amber-400 transition-colors cursor-pointer z-10"
            >
              <i className="ri-close-line text-3xl"></i>
            </button>
            <video 
              autoPlay 
              controls
              className="w-full h-full object-contain"
            >
              <source src="https://www.dropbox.com/scl/fi/7st8ut2bvyn6da5kah2a3/fondation-web-video.mp4?rlkey=4sbmt3w2n8en3zs3nbqxg91b0&st=zq868iur&dl=1" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="relative bg-gray-900 text-white py-16 overflow-hidden min-h-[500px]">
        <div className="absolute inset-0">
          <img 
            src="https://static.readdy.ai/image/82d44354838f3c049f6da9bd8c31e1b1/5f47c77c836e004333a2d82b946d5e21.jpeg" 
            alt="Footer Background" 
            className="w-full h-full object-cover object-center absolute inset-0"
          />
          <div className="absolute inset-0 bg-black/40"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12 items-start">
            <div className="pt-1">
              <img 
                src="https://public.readdy.ai/ai/img_res/3d38b249-c438-4355-9c41-27c4e7e91c1d.png" 
                alt="NexGen Steel" 
                className="h-22"
              />
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/about" className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    About
                  </Link>
                </li>
                <li>
                  <button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    Contact
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Why NexblocX</h4>
              <ul className="space-y-2">
                <li>
                  <button onClick={() => scrollToSection('features')} className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    Our System
                  </button>
                </li>
                <li>
                  <button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    Building with Steel
                  </button>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-bold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Project Gallery</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">FAQ</a></li>
                <li><a href="#" className="text-gray-400 hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 mt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 mb-4 md:mb-0 text-sm text-gray-400">
                <span>All rights reserved 2026 NexblocX Polycrete Systems</span>
                <span className="hidden md:inline">|</span>
                <Link to="/privacy" className="hover:text-white transition-colors">
                  Privacy Policy
                </Link>
                <span>|</span>
                <Link to="/terms" className="hover:text-white transition-colors">
                  Terms & Conditions
                </Link>
                <span>|</span>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">
                  Powered by Readdy
                </a>
              </div>

              <div className="flex items-center space-x-4">
                <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="LinkedIn">
                  <i className="ri-linkedin-fill text-xl"></i>
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="YouTube">
                  <i className="ri-youtube-fill text-xl"></i>
                </a>
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Facebook">
                  <i className="ri-facebook-fill text-xl"></i>
                </a>
                <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="Instagram">
                  <i className="ri-instagram-fill text-xl"></i>
                </a>
                <a href="https://x.com" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors" aria-label="X">
                  <i className="ri-twitter-x-fill text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
